import {
  getHeadersWithUniqueVisitorId,
  retryMessage,
  useBrandAppConfig,
} from "@brandclub/common-ui";
import { useState } from "react";
import { toast, Toaster } from "sonner";
import "./concierge-tailwind.css";
import { ConciergeChatAreaV2 } from "./components/ConciergeChatAreaV2";
import { ConciergeHeader } from "./components/ConciergeHeader";
import { useAutoScroll } from "./hooks/useAutoScroll";
import { useAutoSendFromUrl } from "./hooks/useAutoSendFromUrl";
import { useConversationLoader } from "./hooks/useConversationLoader";
import { useConversationState } from "./hooks/useConversationState";
import { useStreamingSearch } from "./hooks/useStreamingSearch";
import { useUrlSync } from "./hooks/useUrlSync";
import { LightRays } from "@/components/ui/light-rays";
import BagDrawer from "@/components/Checkout/BagDrawer";
import { useCheckoutDrawer } from "@/components/Checkout/useCheckoutDrawer";
import { Drawer } from "@mui/material";

const ConciergeContainer = () => {
  // Brand config
  const { data: config } = useBrandAppConfig();
  const params = new URLSearchParams(window.location.search);
  const brandId = params.get("brandId") || null;

  // Checkout drawer state
  const { isOpen: isCheckoutDrawerOpen } = useCheckoutDrawer();

  // Input state
  const [searchInputValue, setSearchInputValue] = useState("");

  // Conversation state (centralized via hook)
  const conversationState = useConversationState();

  // Auto-scroll behavior
  const {
    chatWrapperRef,
    messagesEndRef,
    shouldScrollOnNextMessageRef,
    scrollToBottom,
  } = useAutoScroll(
    conversationState.conversationHistory.length,
    conversationState.isActivelyStreaming,
    conversationState.streamingAIResponse,
    conversationState.conversationHistory.length === 0
  );

  // Streaming search
  const { performSearch } = useStreamingSearch({
    brandId,
    conversationId: conversationState.conversationId,
    setConversationId: conversationState.setConversationId,
    setIsActivelyStreaming: conversationState.setIsActivelyStreaming,
    setStreamingAIResponse: conversationState.setStreamingAIResponse,
    setCurrentStatus: conversationState.setCurrentStatus,
    setConversationHistory: conversationState.setConversationHistory,
    setIsWaitingForFollowUpQueries:
      conversationState.setIsWaitingForFollowUpQueries,
    setStreamingProductGroups: conversationState.setStreamingProductGroups,
    setProductMetadataMap: conversationState.setProductMetadataMap,
    setPreviewSearchQueries: conversationState.setPreviewSearchQueries,
    setPreviewQueryProducts: conversationState.setPreviewQueryProducts,
    setFollowUpQuestions: conversationState.setFollowUpQuestions,
    setStreamingFeaturedProduct: conversationState.setStreamingFeaturedProduct,
    scrollToBottom,
    shouldScrollOnNextMessageRef,
  });

  // Handle search submission
  const handleSearch = async (searchTerm: string) => {
    setSearchInputValue("");
    await performSearch(searchTerm);
  };

  // Handle retry message
  const handleRetry = async (messageIndex: number) => {
    if (!conversationState.conversationId) {
      toast.error("No conversation ID available");
      return;
    }

    try {
      // Remove messages from the message index onward
      conversationState.setConversationHistory((prev) =>
        prev.slice(0, messageIndex)
      );

      // Set streaming state
      conversationState.setIsActivelyStreaming(true);
      conversationState.setStreamingAIResponse("");
      conversationState.setCurrentStatus("");

      // Get headers
      const headers = await getHeadersWithUniqueVisitorId();

      // Call retry API and stream the response
      await retryMessage({
        conversationId: conversationState.conversationId,
        messageIndex,
        headersToInject: headers,
        onChunk: (chunk) => {
          conversationState.setStreamingAIResponse((prev) => prev + chunk);
        },
        onEvent: (event) => {
          if (event.type === "follow_up_queries") {
            conversationState.setFollowUpQuestions(event.data || []);
            conversationState.setIsWaitingForFollowUpQueries(false);
          }
        },
        onStatusChange: (status) => {
          conversationState.setCurrentStatus(status);
        },
        onComplete: () => {
          // Finalize the streaming message
          const streamedContent = conversationState.streamingAIResponse;
          if (streamedContent) {
            conversationState.setConversationHistory((prev) => [
              ...prev,
              {
                role: "assistant",
                content: streamedContent,
                timestamp: new Date(),
              },
            ]);
          }

          // Reset streaming state
          conversationState.setStreamingAIResponse("");
          conversationState.setIsActivelyStreaming(false);
          conversationState.setCurrentStatus("");
        },
        onError: (error) => {
          console.error("Retry failed:", error);
          toast.error("Failed to retry message");
          conversationState.setIsActivelyStreaming(false);
          conversationState.setStreamingAIResponse("");
        },
        onConversationId: (id) => {
          // Conversation ID should already be set, but update if needed
          if (id !== conversationState.conversationId) {
            conversationState.setConversationId(id);
          }
        },
        onNewProductMetadata: (products) => {
          // Add products to the map
          const newMap = new Map(conversationState.productMetadataMap);
          products.forEach((product) => {
            newMap.set(product.stacklineSku, product);
          });
          conversationState.setProductMetadataMap(newMap);
        },
        onFeaturedProduct: (product, question) => {
          conversationState.setStreamingFeaturedProduct({ product, question });
        },
      });
    } catch (error) {
      console.error("Error retrying message:", error);
      toast.error("Failed to retry message");
      conversationState.setIsActivelyStreaming(false);
    }
  };

  // Handle update feedback
  const handleUpdateFeedback = (
    messageIndex: number,
    feedback: { type: "like" | "dislike"; timestamp: string } | null
  ) => {
    // Update the feedback in the conversation history
    conversationState.setConversationHistory((prev) => {
      return prev.map((msg, idx) => {
        if (idx === messageIndex) {
          return {
            ...msg,
            feedback: feedback || undefined,
          };
        }
        return msg;
      });
    });
  };

  // Handle conversation selection from history
  const handleConversationSelect = (conversationId: string) => {
    // If we're already viewing this conversation, just reload it by updating the ID
    // This ensures a fresh load without the jarring clear + reload
    if (conversationState.conversationId === conversationId) {
      // Force reload by temporarily clearing and resetting the ID
      // This will trigger useConversationLoader to reload
      conversationState.setConversationId(null);
      // Use setTimeout to ensure the state update is processed
      setTimeout(() => {
        conversationState.setConversationId(conversationId);
      }, 0);
      return;
    }

    // Clear current conversation state before loading new one
    conversationState.setConversationHistory([]);
    conversationState.setProductMetadataMap(new Map());
    conversationState.setStreamingProductGroups(new Map());
    conversationState.setPreviewSearchQueries([]);
    conversationState.setPreviewQueryProducts([]);
    conversationState.setIsWaitingForFollowUpQueries(false);
    conversationState.setStreamingAIResponse("");
    conversationState.setCurrentStatus("");
    conversationState.setFollowUpQuestions([]);

    // Update URL with the selected conversation ID
    const params = new URLSearchParams(window.location.search);
    params.set("conversationId", conversationId);
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newUrl);

    // Update conversation state
    conversationState.setConversationId(conversationId);
  };

  // Handle compose button click (go back to welcome screen)
  const handleComposeClick = () => {
    // Clear conversation state
    conversationState.setConversationHistory([]);
    conversationState.setProductMetadataMap(new Map());
    conversationState.setStreamingProductGroups(new Map());
    conversationState.setPreviewSearchQueries([]);
    conversationState.setPreviewQueryProducts([]);
    conversationState.setIsWaitingForFollowUpQueries(false);
    conversationState.setStreamingAIResponse("");
    conversationState.setCurrentStatus("");
    conversationState.setFollowUpQuestions([]);
    conversationState.setConversationId(null);

    // Remove conversationId from URL
    const params = new URLSearchParams(window.location.search);
    params.delete("conversationId");
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.pushState({}, "", newUrl);
  };

  // Conversation loader
  const { isLoadingConversationQuery } = useConversationLoader({
    conversationId: conversationState.conversationId,
    isActivelyStreaming: conversationState.isActivelyStreaming,
    onConversationLoaded: (
      messages,
      productMap,
      followUpQuestions,
      previewSearchQueries,
      previewQueryProducts
    ) => {
      conversationState.setProductMetadataMap(productMap);
      conversationState.setConversationHistory(messages);
      conversationState.setFollowUpQuestions(followUpQuestions);
      conversationState.setPreviewSearchQueries(previewSearchQueries);
      conversationState.setPreviewQueryProducts(previewQueryProducts);
    },
    onConversationNotFound: () => {
      const params = new URLSearchParams(window.location.search);
      params.delete("conversationId");
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
      conversationState.setConversationId(null);
    },
  });

  // Sync conversationId with URL
  useUrlSync(conversationState.conversationId);

  // Auto-send from URL params
  useAutoSendFromUrl({
    isActivelyStreaming: conversationState.isActivelyStreaming,
    conversationLength: conversationState.conversationHistory.length,
    onSearch: handleSearch,
  });

  const showWelcome =
    conversationState.conversationHistory.length === 0 &&
    !isLoadingConversationQuery;

  return (
    <>
      <Toaster />

      <div
        className="concierge-root"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100dvh",
          minHeight: "-webkit-fill-available",
        }}
      >
        <ConciergeHeader
          onConversationSelect={handleConversationSelect}
          onComposeClick={handleComposeClick}
          showComposeIcon={!showWelcome}
        />
        <ConciergeChatAreaV2
          appName={config?.appName}
          brandId={brandId}
          conversationId={conversationState.conversationId}
          conversationHistory={conversationState.conversationHistory}
          productMetadataMap={conversationState.productMetadataMap}
          streamingProductGroups={conversationState.streamingProductGroups}
          previewSearchQueries={conversationState.previewSearchQueries}
          previewQueryProducts={conversationState.previewQueryProducts}
          followUpQuestions={conversationState.followUpQuestions}
          streamingFeaturedProduct={conversationState.streamingFeaturedProduct}
          isActivelyStreaming={conversationState.isActivelyStreaming}
          streamingAIResponse={conversationState.streamingAIResponse}
          currentStatus={conversationState.currentStatus}
          isWaitingForFollowUpQueries={
            conversationState.isWaitingForFollowUpQueries
          }
          isLoadingConversationQuery={isLoadingConversationQuery}
          handleSearch={handleSearch}
          onRetry={handleRetry}
          onUpdateFeedback={handleUpdateFeedback}
          searchInputValue={searchInputValue}
          setSearchInputValue={setSearchInputValue}
        />
        {showWelcome && <LightRays />}
      </div>
      <Drawer
        id="cart-popover"
        open={isCheckoutDrawerOpen}
        sx={{
          zIndex: 1300,
          width: 462,
          maxWidth: "100%",
        }}
        PaperProps={{
          sx: {
            boxShadow: "none",
            borderRadius: "0",
            maxWidth: "100%",
          },
        }}
        anchor="right"
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
        <BagDrawer />
      </Drawer>
    </>
  );
};

export default ConciergeContainer;
